import React, { useState, useEffect } from "react";
import { useBackendVersion } from "@/hooks/useBackendVersion";
import { trackEvent } from "@/lib/analytics";
import { useLocation, Link } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, Eye, EyeOff, ShieldOff, User } from "lucide-react";
import { toast } from "sonner";
import { DEV_USERS, isSkipAuthEnabled } from "@/lib/dev-users";
import { PostLoginRedirect } from "@/components/auth/PostLoginRedirect";
import { EnvironmentLoginNotice } from "@/components/environment/EnvironmentLoginNotice";

const REMEMBER_ME_KEY = "bondIT_remember_me";

// Only the username is remembered. Earlier versions also persisted the
// plaintext password — readable by any XSS — so legacy entries are rewritten
// username-only the moment they're read (#635). Persistent sign-in itself
// comes from the HttpOnly refresh cookie, not from stored credentials.
interface StoredCredentials {
  username: string;
}

interface ReturnLocation {
  pathname?: string;
  search?: string;
  hash?: string;
}

/**
 * Where to send an already-authenticated user: the location the auth guard
 * caught them at, query and hash included, so a deep link such as
 * /admin/consultants?search=Padda survives the auth round-trip (#855).
 */
const returnPath = (state: unknown): string => {
  const from = (state as { from?: ReturnLocation } | null)?.from;
  if (!from?.pathname) {
    return "/";
  }
  return `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`;
};

const LoginPage: React.FC = () => {
  const stored = (() => {
    try {
      const raw = localStorage.getItem(REMEMBER_ME_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as StoredCredentials & { password?: string };
      if ("password" in parsed) {
        localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify({ username: parsed.username }));
      }
      return parsed;
    } catch {
      return null;
    }
  })();

  const [credentials, setCredentials] = useState({
    username: stored?.username ?? "",
    password: "",
    remember: stored !== null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Demo info only shown in production demo environments
  const [showDemoInfo, setShowDemoInfo] = useState(
    import.meta.env.VITE_DEMO_MODE === "true" && import.meta.env.NODE_ENV === "production"
  );

  const { login, devLogin, isAuthenticated } = useAuth();
  const { version: backendVersion } = useBackendVersion();
  const frontendVersion = import.meta.env.VITE_APP_VERSION || "dev";
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    return () => setShowPassword(false);
  }, []);

  // Signed in, or the session restored: go where the user was headed, or to
  // the assignment a shared link points at (#1072).
  if (isAuthenticated) {
    return <PostLoginRedirect fallback={returnPath(location.state)} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowPassword(false);
    setIsLoading(true);

    try {
      await login(credentials);
      if (credentials.remember) {
        localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify({ username: credentials.username }));
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
      trackEvent("login");
      toast.success("Signed in successfully");
    } catch {
      toast.error("Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // The demo-mode quick-fill button uses hardcoded dev passwords. Gated behind
  // `import.meta.env.DEV` so Vite tree-shakes the body — including the literal
  // password strings — out of production bundles. The function still exists as
  // a stub in prod, but its body collapses to the throw, leaving no `"secret"`
  // string for the bundler to ship. See #508 / #516.
  const fillDemoCredentials = (type: string) => {
    // Vitest forces `import.meta.env.DEV=true` and Playwright runs against a
    // dev build, so this throw is unreachable from both unit and e2e tests by
    // design. The bundle-grep guard in Gate 4 provides the actual regression
    // protection. Two ignore markers because unit suite uses v8 coverage,
    // e2e suite uses vite-plugin-istanbul. Same pattern as AuthContext.devLogin.
    /* v8 ignore next 3 */
    /* istanbul ignore next */
    if (!import.meta.env.DEV) {
      throw new Error("fillDemoCredentials is not available in production builds");
    }
    const demoCredentials = {
      admin: { username: "martin.admin@bondit.dk", password: "secret" },
      consultant: { username: "demo.jonas.larsen@bondit.dev", password: "secret" },
    };

    const creds = demoCredentials[type as keyof typeof demoCredentials];
    if (creds) {
      setCredentials(prev => ({ ...prev, ...creds }));
    }
  };

  // The demo-mode UI also displays the password as visible text — gate its
  // rendering on `import.meta.env.DEV` to drop the markup (including the
  // visible `/ secret` label) from production bundles. See #508 / #516.
  const showDemoMode = import.meta.env.DEV && showDemoInfo;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/bondit_collegues_table.jpeg)",
        }}
      >
        {/* Dark overlay for better contrast */}
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center">
                <img
                  src="/bondit-logo-trans-bg.png"
                  alt="BondIT Logo"
                  className="h-12 w-auto filter drop-shadow-lg mb-2"
                />
                <p className="text-sm text-white/90 drop-shadow text-center">
                  Sign in to your consultant portal
                </p>
              </div>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-white/10 border-white/20"
              >
                <Link to="/consultant-signup">Apply as Consultant</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="w-full max-w-4xl mx-auto space-y-6">
            {/* Outside production: which environment this is (#1099). */}
            <EnvironmentLoginNotice />
            {/* Demo Mode Info — dev builds only (tree-shaken out of prod) */}
            {showDemoMode && (
              <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-blue-800">Demo Mode</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-blue-700 mb-3">
                    Use these demo accounts to explore the portal:
                  </p>
                  <div className="grid gap-2">
                    {[
                      { label: "Admin", type: "admin" },
                      { label: "Consultant", type: "consultant" },
                    ].map(({ label, type }) => (
                      <Button
                        key={type}
                        variant="outline"
                        size="sm"
                        onClick={() => fillDemoCredentials(type)}
                        className="justify-start h-8 text-xs border-blue-300 hover:bg-blue-100"
                      >
                        {label}: {type}@bondit.dk / secret
                      </Button>
                    ))}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDemoInfo(false)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Hide demo info
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Skip-auth: quick-select user picker */}
            {isSkipAuthEnabled() ? (
              <div className="max-w-lg mx-auto">
                <Card className="shadow-2xl backdrop-blur-sm bg-white border-white/20">
                  <CardHeader className="text-center pb-4 bg-white">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <ShieldOff className="h-5 w-5 text-amber-500" />
                      <span className="text-xs font-medium uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                        Auth Bypassed
                      </span>
                    </div>
                    <CardTitle className="text-2xl text-gray-900">Quick Login</CardTitle>
                    <CardDescription className="text-gray-600">
                      Select a user to sign in instantly — no password required
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 bg-white">
                    {DEV_USERS.map(devUser => (
                      <button
                        type="button"
                        key={devUser.id}
                        disabled={isLoading}
                        onClick={async () => {
                          setIsLoading(true);
                          try {
                            await devLogin(devUser);
                            trackEvent("dev_login", { role: devUser.role });
                            toast.success(`Signed in as ${devUser.label}`);
                          } catch {
                            toast.error(
                              `Failed to sign in as ${devUser.label}. Is the backend running?`
                            );
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                        className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center transition-colors">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{devUser.label}</p>
                          <p className="text-sm text-gray-500 truncate">{devUser.description}</p>
                        </div>
                        <span className="flex-shrink-0 text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {devUser.role}
                        </span>
                      </button>
                    ))}
                  </CardContent>
                  <CardFooter className="bg-white">
                    <p className="text-xs text-gray-400 text-center w-full">
                      VITE_SKIP_AUTH is enabled — this UI is only available in local development
                    </p>
                  </CardFooter>
                </Card>
              </div>
            ) : (
              /* Normal two-column layout for login + signup */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Login Card */}
                <Card className="shadow-2xl backdrop-blur-sm bg-white border-white/20 dark:bg-white dark:border-white/20">
                  <form onSubmit={handleSubmit}>
                    <CardHeader className="text-center pb-6 bg-white dark:bg-white">
                      <CardTitle className="text-2xl text-gray-900 dark:text-gray-900">
                        Sign In
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-600">
                        Access your consultant dashboard
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 bg-white dark:bg-white">
                      <div className="space-y-2">
                        <Label htmlFor="username" className="text-gray-700 dark:text-gray-700">
                          Email or Username
                        </Label>
                        <Input
                          id="username"
                          name="username"
                          type="text"
                          value={credentials.username}
                          onChange={handleInputChange}
                          placeholder="Enter your username"
                          required
                          autoComplete="username"
                          className="bg-white text-gray-900 border-gray-300 focus:border-blue-500 dark:bg-white dark:text-gray-900 dark:border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-gray-700 dark:text-gray-700">
                            Password
                          </Label>
                          <Link
                            to="/forgot-password"
                            className="inline-block py-1.5 -my-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Forgot password?
                          </Link>
                        </div>
                        <div className="relative">
                          <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={credentials.password}
                            onChange={handleInputChange}
                            placeholder="Enter your password"
                            required
                            autoComplete="current-password"
                            className="bg-white text-gray-900 border-gray-300 focus:border-blue-500 dark:bg-white dark:text-gray-900 dark:border-gray-300 pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(prev => !prev)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {/* The whole row is the tap target, not just the
                            13px checkbox (#996). */}
                        <label
                          htmlFor="remember"
                          className="flex w-fit cursor-pointer items-center gap-2 py-1.5 -my-1.5"
                        >
                          <input
                            id="remember"
                            name="remember"
                            type="checkbox"
                            checked={credentials.remember}
                            onChange={handleInputChange}
                            className="h-4 w-4 rounded border-input"
                          />
                          <span className="text-sm font-medium leading-none text-gray-700 dark:text-gray-700">
                            Remember me
                          </span>
                        </label>
                        {credentials.remember && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 pl-6">
                            We&apos;ll remember your email address on this device so you don&apos;t
                            have to type it next time. Your password is never stored.
                          </p>
                        )}
                      </div>
                    </CardContent>
                    <CardFooter className="bg-white dark:bg-white">
                      <Button type="submit" disabled={isLoading} className="w-full" size="lg">
                        {isLoading ? "Signing in..." : "Sign In"}
                      </Button>
                    </CardFooter>
                  </form>
                </Card>

                {/* Apply as Consultant Card */}
                <Card className="shadow-2xl backdrop-blur-sm bg-white border-white/20 dark:bg-white dark:border-white/20">
                  <CardHeader className="text-center pb-6 bg-white dark:bg-white">
                    <CardTitle className="text-2xl text-gray-900 dark:text-gray-900">
                      Join Our Network
                    </CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-600">
                      Start your journey as a consultant
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 bg-white dark:bg-white">
                    <div className="text-center space-y-4">
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-900">
                          Ready to Apply?
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-600">
                          Join our network of skilled consultants and access exciting opportunities.
                        </p>
                      </div>
                      <ul className="text-left text-sm text-gray-600 dark:text-gray-600 space-y-2">
                        <li>• Access to premium assignments</li>
                        <li>• Competitive rates</li>
                        <li>• Flexible working arrangements</li>
                        <li>• Professional support team</li>
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter className="bg-white dark:bg-white">
                    <Button asChild className="w-full" size="lg" variant="outline">
                      <Link to="/consultant-signup">Apply as Consultant</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white/10 backdrop-blur-sm border-t border-white/20 py-6 mt-16">
          <div className="container mx-auto px-4">
            <div className="text-center space-y-3">
              <div>
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="text-white/90 hover:text-white hover:bg-white/10"
                >
                  <Link to="/" className="inline-flex items-center">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Home
                  </Link>
                </Button>
              </div>
              <p className="text-sm text-white/80">
                © {currentYear} BondIT Denmark ApS. All rights reserved.{" "}
                <span title="Frontend version">UI {frontendVersion}</span>
                {backendVersion && <span title="Backend version"> · API {backendVersion}</span>}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
